// src/User.js
import { supabase } from "./supabaseClient";

// Shared starting balance constant
export const STARTING_CASH = 10000;

/**
 * Ensures a user profile record exists in the public "users" table.
 * If it doesn't exist, it creates one with starting cash and an empty portfolio.
 */
export const ensureUserProfile = async (user) => {
  if (!user) return null;

  try {
    const { data: profile, error: fetchError } = await supabase
      .from("users")
      .select("*")
      .eq("id", user.id)
      .maybeSingle();

    if (fetchError && fetchError.code !== "PGRST116") {
      console.error("Error fetching user profile:", fetchError);
    }

    if (!profile) {
      const email = user.email || "";
      const defaultUsername =
        user.user_metadata?.full_name ||
        user.user_metadata?.name ||
        email.split("@")[0] ||
        "Trader";

      const newProfile = {
        id: user.id,
        email: email,
        display_name: defaultUsername,
        username: defaultUsername,
        created_at: new Date().toISOString(),
        cash: STARTING_CASH,
        portfolio: {},
      };

      const { data: insertedProfile, error: insertError } = await supabase
        .from("users")
        .insert(newProfile)
        .select()
        .single();

      if (insertError) {
        console.error("Error creating user profile:", insertError);
        return null;
      }

      return insertedProfile;
    }

    return profile;
  } catch (error) {
    console.error("Error ensuring user profile:", error);
    return null;
  }
};

// Sign in with Google
export const loginWithGoogle = async () => {
  try {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: window.location.origin,
      },
    });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error signing in with Google:", error);
  }
};

// Sign out
export const logoutUser = async () => {
  const { error } = await supabase.auth.signOut();
  if (error) {
    console.error("Error signing out:", error);
  }
};

// Listen to auth state changes
export const listenToAuthChanges = (callback) => {
  const { data: authListener } = supabase.auth.onAuthStateChange(
    async (event, session) => {
      const user = session?.user || null;

      if (user) {
        const profile = await ensureUserProfile(user);

        // Merge Auth user and Database profile data
        // Include `uid` alias for backwards compatibility with Firebase references
        callback({
          ...user,
          uid: user.id,
          ...profile,
        });
      } else {
        callback(null);
      }
    }
  );

  // Return unsubscribe function matching Firebase interface pattern
  return () => {
    authListener.subscription.unsubscribe();
  };
};